import React, { useCallback } from "react";
import { PublicPagePopupsProvider, Text, Link } from "iparque-components";
import { useTranslation } from "react-i18next";
import useLocalizedUrls from "hooks/useLocalizedUrls";
import useWarnings from "hooks/useWarnings";
import useCookies from "hooks/useCookies";
import styled, { css } from "styled-components";
import { pushDataLayerEvent, updateConsent } from "utils/gtag";

const POPUPS_DELAY = 1.5;

interface WrapperProps {
  isCookiesAccepted: boolean;
}

const PagePopups: React.FC = ({ children }) => {
  const { t } = useTranslation();
  const { privacyPolicyUrl } = useLocalizedUrls();
  const warnings = useWarnings();
  const { isCookiesDecisionMade, handleAcceptCookies, handleRejectCookies } =
    useCookies();

  const acceptCookiesAndUpdateConsent = useCallback(() => {
    handleAcceptCookies();
    pushDataLayerEvent("cookie_consent_update", { consent: "granted" });
    updateConsent(true);
  }, [handleAcceptCookies]);

  const rejectCookiesAndUpdateConsent = useCallback(() => {
    handleRejectCookies();
    pushDataLayerEvent("cookie_consent_update", { consent: "denied" });
    updateConsent(false);
  }, [handleRejectCookies]);

  return (
    <Wrapper isCookiesAccepted={isCookiesDecisionMade}>
      <PublicPagePopupsProvider
        popupsDelay={POPUPS_DELAY}
        warnings={warnings}
        cookies={
          isCookiesDecisionMade
            ? undefined
            : {
                title: t("14382") /* Utilização de cookies */,
                body: (
                  <>
                    <Text variant="body7" color="dark">
                      {
                        t(
                          "14383"
                        ) /* O iParque, detido pela ACIN, utiliza cookies para melhorar a sua experiência e analisar o tráfego do website. Clique em ‘Aceitar’ para permitir a utilização de cookies ou em ‘Recusar’ para manter apenas os cookies essenciais. Saiba mais na nossa */
                      }
                      <StyledLink href={privacyPolicyUrl} target="_blank">
                        {t("14384") /* Política de Cookies */}
                      </StyledLink>
                      .
                    </Text>
                  </>
                ),
                button: {
                  confirmLabel: t("6387") /* Aceitar */,
                  cancelLabel: t("9995") /* Recusar */,
                  onConfirm: acceptCookiesAndUpdateConsent,
                  onCancel: rejectCookiesAndUpdateConsent,
                },
              }
        }
      >
        {children}
      </PublicPagePopupsProvider>
    </Wrapper>
  );
};

const Wrapper = styled.div<WrapperProps>`
  ${({ isCookiesAccepted }) =>
    !isCookiesAccepted &&
    css`
      @media (max-width: 1400px) {
        > div:first-child {
          width: 100%;
        }
      }
    `}
`;

export default PagePopups;

const StyledLink = styled(Link)`
  font-weight: bold;
`;
